import React, { useEffect, useState } from 'react';
import ElementPalette from './ElementPalette';
import ElementProperties from './ElementProperties';
import CanvasArea from './CanvasArea';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';

import { Col, Row } from 'reactstrap';
import { isEqual } from 'lodash'; // Using lodash for deep comparison
import { isTouchDevice } from '../../../utils';
import { H5 } from '../../../AbstractElements';

interface Element {
  id: number;
  type: string;
  subject: string;
  description: string;
  file_id?: string;
}

interface PageCreatorProps {
  setElementsData: (elements: Element[]) => void;
  elementsData: Element[];
  setFiles: (elements: any[]) => void;
  files: PageFile[];
  name: string;
  setName: (name: string) => void;
}

export interface PageFile {
  id: string;
  file: DropZoneFile | null;
}

interface DropZoneFile {
  file: File;
  name: string | undefined;
  type: string | undefined;
  valid: boolean;
}

const PageCreator: React.FC<PageCreatorProps> = ({ setFiles, elementsData, files, setElementsData, name, setName }) => {
  const backend = isTouchDevice() ? TouchBackend : HTML5Backend; // Choose backend based on device capability
  const [elements, setElements] = useState<Element[]>(elementsData);
  const [selectedElement, setSelectedElement] = useState<Element | null>(null);
  const [pageFiles, setPageFiles] = useState<PageFile[]>([]);

  // Compare and update pageFiles only if they differ from files
  useEffect(() => {
    if (!isEqual(pageFiles, files)) {
      setPageFiles(files);
    }
  }, [files]);

  useEffect(() => {
    setElements(elementsData);
  }, [elementsData]);

  useEffect(() => {
    setElementsData(elements)
  }, [elements])

  // Update external state whenever pageFiles change
  useEffect(() => {
    setFiles(pageFiles);
  }, [pageFiles, setFiles]);

  const addElement = (element: Element) => {
    const updatedElements = [...elements, element];
    setElements(updatedElements);
  };

  const updateElement = (updatedElement: Element) => {
    const updatedElements = elements.map(el => el.id === updatedElement.id ? updatedElement : el);
    setElements(updatedElements);
    setSelectedElement(updatedElement);
  };

  return (
    <DndProvider backend={backend}>
      <Row style={{ display: 'flex' }} className='page-creator'>
        <Col lg={3} className='d-flex flex-column align-items-center'>
          <div style={{ width: 250 }}>
            <ElementPalette addElement={addElement} elements={elements} name={name} setName={setName}/>
          </div>
          {selectedElement
            &&
            <ElementProperties
              pageFiles={pageFiles}
              setPageFiles={setPageFiles}
              elements={elements}
              setElements={setElements}
              setSelectedElement={setSelectedElement}
              element={selectedElement}
              onUpdate={updateElement} />}
        </Col>
        <Col lg={9}>
          <CanvasArea
            elements={elements}
            pageFiles={pageFiles}
            selectedElement={selectedElement}
            setElements={setElements}
            setSelectedElement={setSelectedElement}
          />
        </Col>
      </Row>
    </DndProvider>
  );
};

export default PageCreator;
